import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UpdateEmailResource } from './update-email.resource';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, finalize, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Throttle } from '../../../../common/throttle';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../common/services/toast-service/toast-service.service';
import { IonicColor } from '../../../../common/entities/toast/ionic-color';
import { LoadingService } from '../../../../common/services/loading/loading.service';

@Injectable({
    providedIn: 'root',
})
export class UpdateEmailService {
    private readonly model$ = new BehaviorSubject<UpdateEmailResource>(null);
    protected readonly refreshVerificationThrottle$ = new Throttle<UpdateEmailResource>(10000);

    readonly updateEmail$ = this.model$.pipe(distinctUntilChanged());

    constructor(
        private readonly http: HttpClient,
        private readonly toastService: ToastService,
        private readonly translateService: TranslateService,
        private readonly loadingService: LoadingService,
    ) {
        this.refreshVerificationThrottle$.value$.pipe(takeUntilDestroyed()).subscribe({
            next: (it) => {
                this.toastService.showToast(
                    this.translateService.instant('UPDATE_EMAIL.VERIFICATION_REFRESH_SUCCESS'),
                    IonicColor.success,
                );
                this.model$.next(it);
            },
            error: () =>
                this.toastService.showToast(
                    this.translateService.instant('UPDATE_EMAIL.VERIFICATION_REFRESH_FAILED'),
                    IonicColor.danger,
                ),
        });
    }

    get current() {
        return this.model$.value;
    }

    fetchCurrent(username: string) {
        if (username === this.model$.value?.username) {
            this.model$.next({ ...this.model$.value });
        } else {
            this.http
                .get<UpdateEmailResource>(`user/${username}/update-email-events/latest`)
                .subscribe((it) => this.model$.next(it));
        }
    }

    initiate(username: string, emailUpdate: string) {
        return this.http
            .post<UpdateEmailResource>(`user/${username}/update-email-events`, {
                email: emailUpdate,
            })
            .pipe(
                tap(
                    (it) => {
                        this.toastService.showToast(
                            this.translateService.instant('UPDATE_EMAIL.INITIATION_SUCCESS'),
                            IonicColor.success,
                        );
                        this.model$.next(it);
                    },
                    () =>
                        this.toastService.showToast(
                            this.translateService.instant('UPDATE_EMAIL.INITIATION_FAILED'),
                            IonicColor.danger,
                        ),
                ),
            );
    }

    refreshVerification(username: string) {
        this.refreshVerificationThrottle$.next(() => {
            this.loadingService.startLoadingModal('UPDATE_EMAIL.LOADING_VERIFICATION_REFRESH');
            return this.http
                .patch<UpdateEmailResource>(`user/${username}/update-email-events/latest`, {})
                .pipe(finalize(() => this.loadingService.stopLoadingModal()));
        });
    }

    verify(username: string, id: string, emailUpdate: string) {
        return this.http
            .put<UpdateEmailResource>(`user/${username}/update-email-events/${id}`, {
                email: emailUpdate,
            })
            .pipe(
                tap(
                    (it) => {
                        this.toastService.showToast(
                            this.translateService.instant('UPDATE_EMAIL.VERIFICATION_SUCCESS'),
                            IonicColor.success,
                        );
                        this.model$.next(it);
                    },
                    (it) =>
                        this.toastService.showToast(
                            this.translateService.instant(
                                it.error.message?.includes('UPDATE_EMAIL.')
                                    ? it.error.message
                                    : 'UPDATE_EMAIL.VERIFICATION_FAILED',
                            ),
                            IonicColor.danger,
                        ),
                ),
            );
    }

    clear() {
        this.model$.next(null);
    }
}
